import React from 'react'
import PropTypes from 'prop-types'
import { Alert, Box, LinearProgress, Stack, Typography } from '@mui/material'

const phaseLabel = phase => {
  if (phase === 'PROBING') return 'Preparing video'
  if (phase === 'TRANSCODING') return 'Transcoding video'
  if (phase === 'UPLOADING') return 'Finishing video'
  if (phase === 'COMPLETE') return 'Ready'
  if (phase === 'QUEUED') return 'Queued for processing'
  return 'Processing video'
}

const uploadStatusLabel = upload => {
  if (!upload) return 'Unknown'
  if (upload.readyForPlayback) return 'Ready'
  if (upload.status === 'ready') return 'Playback unavailable'
  if (upload.status === 'failed') {
    return upload.processingErrorCode === 'UPLOAD_INCOMPLETE'
      ? 'Upload incomplete'
      : 'Processing failed'
  }
  if (upload.status === 'pending_upload') return 'Waiting for upload'
  if (upload.status === 'queued') return 'Queued for processing'
  if (upload.status === 'submitting') return 'Starting processing'
  if (upload.status === 'processing') {
    const label = phaseLabel(upload.processingPhase)
    return Number.isFinite(upload.processingPercent)
      ? `${label} · ${upload.processingPercent}%`
      : label
  }
  return upload.status || 'Processing'
}

const userMessage = upload => {
  if (!upload) return ''
  if (upload.status === 'failed') {
    if (upload.processingErrorCode === 'UPLOAD_INCOMPLETE') {
      return 'The source video did not finish uploading. Retry the upload or ask an admin to reconcile it.'
    }
    return 'Video processing failed. An admin can inspect the job and retry processing.'
  }
  if (upload.status === 'ready' && !upload.readyForPlayback) {
    return 'Processing completed, but playback is unavailable. An admin can reconcile this upload.'
  }
  if (upload.status === 'pending_upload') {
    return 'Waiting for the source video to finish uploading.'
  }
  if (upload.status === 'queued') {
    return 'The source video is safely uploaded and waiting for processing to start.'
  }
  if (upload.status === 'submitting') {
    return 'The processing job is being submitted.'
  }
  if (upload.status === 'processing') {
    const phase = phaseLabel(upload.processingPhase)
    return Number.isFinite(upload.processingPercent)
      ? `${phase}. ${upload.processingPercent}% complete.`
      : `${phase}. Playback will appear when it is ready.`
  }
  return 'Playback will appear when the video is ready.'
}

function UploadProcessingStatus ({ upload, compact = false }) {
  if (!upload || upload.readyForPlayback) return null

  const failed = upload.status === 'failed'
  const warning = upload.status === 'ready' && !upload.readyForPlayback
  const percent = Number.isFinite(upload.processingPercent)
    ? Math.max(0, Math.min(100, upload.processingPercent))
    : null

  return (
    <Alert severity={failed ? 'error' : warning ? 'warning' : 'info'} variant={compact ? 'outlined' : 'standard'}>
      <Stack spacing={0.75}>
        <Typography variant={compact ? 'caption' : 'body2'} sx={{ fontWeight: 700 }}>
          {uploadStatusLabel(upload)}
        </Typography>
        {!compact
          ? <Typography variant="body2">
              {userMessage(upload)}
            </Typography>
          : null}
        {!failed && percent !== null
          ? <Box sx={{ minWidth: compact ? 120 : 240 }}>
              <LinearProgress variant="determinate" value={percent} />
            </Box>
          : null}
      </Stack>
    </Alert>
  )
}

UploadProcessingStatus.propTypes = {
  upload: PropTypes.object,
  compact: PropTypes.bool
}

export { uploadStatusLabel, userMessage }
export default UploadProcessingStatus
